import React, { useState, useEffect } from 'react';
import { Box, Button, Tooltip, Paper } from '@mui/material';
import { RequestPage } from '@mui/icons-material';
import EditReportModule from './EditReportModule';
import EditMonthReportModule from './EditMonthReportModule';
import ViewReportModule from './ViewReportModule';
import ViewReportModuleDir from './ViewReportModuleDir';
import UsersPerDay from './CheckUsersOnDay';
import Compare from './CompareModule';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { usePermissions } from '../../../utils/usePermissions';

type ModuleType = 'edit' | 'editnew' | 'view' | 'empty' | 'compare' | 'MathReport' | 'editmonth';

interface ReportsProps {
  setSelectedIndex: (index: number) => void;
  selectedModule?: ModuleType;
}

const Reports: React.FC<ReportsProps> = ({ setSelectedIndex, selectedModule }) => {
  const { hasPerm } = usePermissions();

  const canFillMonitoring = hasPerm('reports.fill_monitoring');
  const canEditDaily = hasPerm('reports.edit_daily');
  const canEditMonthly = hasPerm('reports.edit_monthly');
  const canViewSummary = hasPerm('reports.view_summary');
  const canViewDirector = hasPerm('reports.view_director');
  const canViewDelivery = hasPerm('reports.delivery.view');
  const canMathReport = hasPerm('reports.math.view');

  const getInitialModule = (): ModuleType | null => {
    if (canEditDaily) return 'edit';
    if (canViewDirector) return 'editnew';
    if (canViewSummary) return 'view';
    if (canFillMonitoring) return 'empty';
    if (canEditMonthly) return 'editmonth';
    if (canViewDelivery) return 'compare';
    if (canMathReport) return 'MathReport';
    return null;
  };

  const [activeModule, setActiveModule] = useState<ModuleType | null>(selectedModule || getInitialModule());

  useEffect(() => {
    if (selectedModule) {
      setActiveModule(selectedModule);
    }
  }, [selectedModule]);

  // Проверка актуальности активного модуля при изменении прав
  useEffect(() => {
    if (activeModule === 'edit' && !canEditDaily) setActiveModule(getInitialModule());
    else if (activeModule === 'editnew' && !canViewDirector) setActiveModule(getInitialModule());
    else if (activeModule === 'view' && !canViewSummary) setActiveModule(getInitialModule());
    else if (activeModule === 'empty' && !canFillMonitoring) setActiveModule(getInitialModule());
    else if (activeModule === 'editmonth' && !canEditMonthly) setActiveModule(getInitialModule());
    else if (activeModule === 'compare' && !canViewDelivery) setActiveModule(getInitialModule());
    else if (activeModule === 'MathReport' && !canMathReport) setActiveModule(getInitialModule());
    else if (!activeModule) setActiveModule(getInitialModule());
  }, [canFillMonitoring, canEditDaily, canEditMonthly, canViewSummary, canViewDirector, canViewDelivery, canMathReport, activeModule]);

  const hasAnyReportTab = canFillMonitoring || canEditDaily || canEditMonthly || canViewSummary || canViewDirector || canViewDelivery || canMathReport;

  if (!hasAnyReportTab) {
    return null;
  }

  const handleMathReportClick = () => {
    setSelectedIndex(6);
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      {/* Навигационная панель вкладок отчетов */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: 1,
          display: 'inline-flex',
          flexWrap: 'wrap',
          gap: 1,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '12px'
        }}
      >
        {canFillMonitoring && (
          <Tooltip title="Мониторинг сдачи ежедневных отчетов аптеками" enterDelay={800} arrow>
            <Button
              variant={activeModule === 'empty' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setActiveModule('empty')}
              startIcon={<PermContactCalendarIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.8,
                boxShadow: activeModule === 'empty' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                color: activeModule === 'empty' ? '#ffffff' : '#64748b'
              }}
            >
              Заполнение
            </Button>
          </Tooltip>
        )}

        {canEditDaily && (
          <Tooltip title="Редактирование показателей отчета за день" enterDelay={800} arrow>
            <Button
              variant={activeModule === 'edit' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setActiveModule('edit')}
              startIcon={<DriveFileRenameOutlineIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.8,
                boxShadow: activeModule === 'edit' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                color: activeModule === 'edit' ? '#ffffff' : '#64748b'
              }}
            >
              Редактировать
            </Button>
          </Tooltip>
        )}

        {canEditMonthly && (
          <Tooltip title="Редактирование месячной шахматки показателей" enterDelay={800} arrow>
            <Button
              variant={activeModule === 'editmonth' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setActiveModule('editmonth')}
              startIcon={<CalendarMonthIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.8,
                boxShadow: activeModule === 'editmonth' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                color: activeModule === 'editmonth' ? '#ffffff' : '#64748b'
              }}
            >
              Редактирование (мес)
            </Button>
          </Tooltip>
        )}

        {canViewSummary && (
          <Tooltip title="Просмотр сводного отчета за выбранный период" enterDelay={800} arrow>
            <Button
              variant={activeModule === 'view' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setActiveModule('view')}
              startIcon={<DescriptionIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.8,
                boxShadow: activeModule === 'view' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                color: activeModule === 'view' ? '#ffffff' : '#64748b'
              }}
            >
              Отчет
            </Button>
          </Tooltip>
        )}

        {canViewDirector && (
          <Tooltip title="Управленческая сводка по подчиненным аптекам" enterDelay={800} arrow>
            <Button
              variant={activeModule === 'editnew' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setActiveModule('editnew')}
              startIcon={<DescriptionIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.8,
                boxShadow: activeModule === 'editnew' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                color: activeModule === 'editnew' ? '#ffffff' : '#64748b'
              }}
            >
              Дир. отчет
            </Button>
          </Tooltip>
        )}

        {canViewDelivery && (
          <Tooltip title="Сверка и сопоставление файлов доставки" enterDelay={800} arrow>
            <Button
              variant={activeModule === 'compare' ? 'contained' : 'text'}
              color="primary"
              onClick={() => setActiveModule('compare')}
              startIcon={<TableChartIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.8,
                boxShadow: activeModule === 'compare' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                color: activeModule === 'compare' ? '#ffffff' : '#64748b'
              }}
            >
              Доставка
            </Button>
          </Tooltip>
        )}

        {canMathReport && (
          <Tooltip title="Материальные отчеты аптек" enterDelay={800} arrow>
            <Button
              variant={activeModule === 'MathReport' ? 'contained' : 'text'}
              color="primary"
              onClick={handleMathReportClick}
              startIcon={<RequestPage />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 0.8,
                boxShadow: activeModule === 'MathReport' ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
                color: activeModule === 'MathReport' ? '#ffffff' : '#64748b'
              }}
            >
              Мат. отчет
            </Button>
          </Tooltip>
        )}
      </Paper>

      {/* Контент активного подмодуля */}
      {activeModule === 'empty' && canFillMonitoring && <UsersPerDay />}
      {activeModule === 'edit' && canEditDaily && <EditReportModule />}
      {activeModule === 'editmonth' && canEditMonthly && <EditMonthReportModule />}
      {activeModule === 'view' && canViewSummary && <ViewReportModule />}
      {activeModule === 'editnew' && canViewDirector && <ViewReportModuleDir />}
      {activeModule === 'compare' && canViewDelivery && <Compare />}
    </Box>
  );
};

export default Reports;